import { Icon } from '@iconify/react'
import React from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './components/AuthContext'
import { Layout } from './components/Layout'
import { ChangelogPage } from './pages/ChangelogPage'
import { DataPanel } from './pages/DataPanel'
import { InstallPage } from './pages/InstallPage'
import { LoginPage } from './pages/LoginPage'
import { QueryConsole } from './pages/QueryConsole'
import { AdminPage } from './pages/AdminPage'
import { RegisterPage } from './pages/RegisterPage'

const HELLO_SOURCE = 'myscope-webpanel'
const HELLO_TYPE = 'MYSCOPE_EXTENSION_HELLO'
const ACK_TYPE = 'MYSCOPE_EXTENSION_HELLO_ACK'

type ExtensionGate =
  | { status: 'checking' }
  | { status: 'blocked' }
  | { status: 'ready'; userId: string }

function ExtensionGateCheck({ children }: { children: React.ReactNode }) {
  const [gate, setGate] = React.useState<ExtensionGate>({ status: 'checking' })
  const location = useLocation()
  const { state: authState } = useAuth()

  React.useEffect(() => {
    let done = false

    const fromMarker = () => {
      const root = document.documentElement
      if (root.getAttribute('data-myscope-extension-present') !== '1') return null
      const uid = root.getAttribute('data-myscope-extension-user-id')?.trim() || 'unknown-user'
      return uid
    }

    const markerUserId = fromMarker()
    if (markerUserId) {
      setGate({ status: 'ready', userId: markerUserId })
      return
    }

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window) return
      const data = event.data as { source?: string; type?: string; payload?: { userId?: string } }
      if (!data || data.source !== HELLO_SOURCE || data.type !== ACK_TYPE) return

      if (done) return
      done = true
      const userId = data.payload?.userId?.trim() || 'unknown-user'
      setGate({ status: 'ready', userId })
    }

    window.addEventListener('message', onMessage)

    const sendHello = () => {
      window.postMessage({ source: HELLO_SOURCE, type: HELLO_TYPE }, '*')
    }

    sendHello()
    const helloTicker = window.setInterval(sendHello, 250)

    const timeout = window.setTimeout(() => {
      if (done) return
      done = true
      setGate({ status: 'blocked' })
    }, 2500)

    return () => {
      window.removeEventListener('message', onMessage)
      window.clearInterval(helloTicker)
      window.clearTimeout(timeout)
    }
  }, [])

  if (gate.status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-4xl p-6">
          <div className="panel text-center">
            <h1 className="text-xl font-semibold">Checking extension access...</h1>
            <p className="mt-2 text-sm text-slate-300">Waiting for MyScope extension handshake.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {gate.status === 'blocked' && (
        <div className="mx-auto mb-4 max-w-4xl px-6 pt-6">
          <div className="viewer-badge flex items-center gap-2">
            <Icon icon="mdi:eye-off-outline" width="16" />
            <span>
              Extension not detected.{' '}
              <a className="underline" href="/install">Install</a> or{' '}
              <button
                className="underline"
                onClick={() => window.location.reload()}
              >
                reload
              </button>{' '}
              after loading.
            </span>
            <span className="ml-auto text-xs opacity-70">viewing as viewer</span>
          </div>
        </div>
      )}
      {children}
    </>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { state } = useAuth()

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-center text-slate-300">
        Loading...
      </div>
    )
  }

  if (state.status === 'unauthenticated') {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  const { state: authState } = useAuth()
  const viewerUserId = authState.status === 'authenticated' ? authState.userId : 'viewer'

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={
        authState.status === 'authenticated' ? <Navigate to="/panel" replace /> : <LoginPage />
      } />
      <Route path="/register" element={
        authState.status === 'authenticated' ? <Navigate to="/panel" replace /> : <RegisterPage />
      } />

      {/* Install page (public) */}
      <Route path="/install" element={<InstallPage />} />

      {/* Panel routes (auth required) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ExtensionGateCheck>
              <Layout />
            </ExtensionGateCheck>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/panel" replace />} />
        <Route path="panel" element={<DataPanel viewerUserId={viewerUserId} />} />
        <Route path="query" element={<QueryConsole />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="changelog" element={<ChangelogPage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}
